import * as angular from 'angular';
import { TransmissionSystemOperatorResource } from 'app/_resources/transmission-system-operator.resource';
import { AuthService } from 'app/_services/auth.service';
import { ConfirmModal } from 'app/_services/confirm-modal.factory';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import { UtilService } from 'app/_services/util.service';

class OperatorSettingsSecondaryMarketComponentCtrl implements ng.IController {
    private settings: TsoSecondarySettingsDto;
    public enableTradeNote: boolean = false;
    public togglingTradeNote: boolean;
    private authenticatedUser: LoginResult;
    private tradingConditions: TradingConditionsSecondarySettingsDto[];
    private minDate: Date;
    private enableLeadTimes: boolean = false;
    private leadPanelExpanded: boolean = false;
    private tradingDetailsExpanded: boolean = false;
    private loading: boolean;
    private loadingSettingsErrorMsg: string;
    private loadingTradingConditionsErrorMsg: string;
    private loadingFlexibleLeadTimeRulesErrorMsg: string;
    private enableAdditionalConditions: boolean;

    constructor(
        private TransmissionSystemOperator: TransmissionSystemOperatorResource,
        private authService: AuthService,
        private $q: ng.IQService,
        private errorHandler: ErrorHandler,
        private utilService: UtilService,
        private confirmModal: ConfirmModal
    ) {}

    $onInit(): void {
        $('[data-toggle="popover"]').popover();
        this.minDate = new Date();
        this.loading = true;
        this.authService.userLoginPromise.promise.then(
            (loginResult: LoginResult) => {
                this.authenticatedUser = loginResult;
                this.queryCurrentSettings();
            }
        );
    }

    private queryCurrentSettings() {
        const tsoRef = { id: this.authenticatedUser.referenceId };
        const promiseSettings =
            this.TransmissionSystemOperator.getSecondarySettings(
                tsoRef,
                (res: TsoSecondarySettingsDto) => {
                    this.settings = res;
                    this.enableLeadTimes = !!this.settings.leadTimeRules.length;
                    this.enableTradeNote =
                        this.settings.tradeNote != null &&
                        this.settings.tradeNote.length > 0;
                    this.loadingSettingsErrorMsg = null;
                },
                (err: any) => {
                    this.loadingSettingsErrorMsg =
                        err && err.data && err.data.message
                            ? err.data.message
                            : 'Something went wrong when loading the auction settings. Please check back later.';
                }
            ).$promise;

        const promiseTradingConditions =
            this.TransmissionSystemOperator.querySecondarySettingsTradingConditions(
                tsoRef,
                (res: TradingConditionsSecondarySettingsDto[]) => {
                    this.tradingConditions = res;
                    this.loadingTradingConditionsErrorMsg = null;
                    this.enableAdditionalConditions = !!res.length;
                },
                (err: any) => {
                    this.loadingTradingConditionsErrorMsg =
                        err && err.data && err.data.message
                            ? err.data.message
                            : 'Something went wrong when loading the trading conditions. Please check back later.';
                }
            ).$promise;

        this.$q
            .all([promiseSettings, promiseTradingConditions])
            .finally(() => (this.loading = false));
    }

    public saveTradeNote(): ng.IPromise<any> {
        return this.TransmissionSystemOperator.saveSecondaryTradeNote(
            { id: this.authenticatedUser.referenceId },
            this.settings.tradeNote
        ).$promise;
    }

    public toggleLeadTimesEnabled(): ng.IPromise<any> | void {
        this.enableLeadTimes = !this.enableLeadTimes;
        this.confirmModal(
            'By disabling it, you will also erase all the current selections and entries.',
            'Are you sure you want to disable this setting?'
        ).result.then(() => {
            this.TransmissionSystemOperator.disableSecondaryFlexibleLeadTimeRules(
                { id: this.authenticatedUser.referenceId },
                () => {
                    this.settings.leadTimeRules = [];
                    this.settings.leadTimeDescription = null;
                    this.enableLeadTimes = !this.enableLeadTimes;
                },
                this.errorHandler
            );
        });
    }

    disableAdditionalConditions() {
        this.enableAdditionalConditions = !this.enableAdditionalConditions;
        this.confirmModal(
            'By confirming all your current additional trading conditions will be deleted.'
        ).result.then(() => {
            this.TransmissionSystemOperator.deleteAllAdditionalConditions(
                { id: this.authenticatedUser.referenceId },
                () => {
                    this.tradingConditions = [];
                    this.enableAdditionalConditions =
                        !this.enableAdditionalConditions;
                },
                this.errorHandler
            );
        });
    }

    public toggleTradeNote() {
        this.enableTradeNote = !this.enableTradeNote;

        var title = this.enableTradeNote
            ? 'Are you sure you want to disable this setting?'
            : 'Are you sure you want to enable this setting?';
        var text = this.enableTradeNote
            ? 'By disabling it, you will also erase the current note for shippers.'
            : 'After enabling it, you can provide a note for the shippers.';
        this.confirmModal(text, title).result.then(() => {
            this.togglingTradeNote = true;
            this.settings.tradeNote = null;
            return this.TransmissionSystemOperator.saveSecondaryTradeNote(
                { id: this.authenticatedUser.referenceId },
                this.settings.tradeNote,
                () => (this.enableTradeNote = !this.enableTradeNote)
            ).$promise.finally(() => (this.togglingTradeNote = false));
        });
    }

    public scrollToAnchor(anchorId: string) {
        this.tradingDetailsExpanded = true;
        this.utilService.scrollToAnchor(anchorId, 800, 80, 500);
    }
}

angular.module('app').component('pkpOperatorSettingsSecondaryMarket', {
    controller: OperatorSettingsSecondaryMarketComponentCtrl,
    template:
        require('app/operator-settings-secondary-market/operator-settings-secondary-market.html'),
    bindings: {
        enabledSettings: '=',
        balancingGroupPortfolioCodeSelection: '<'
    }
});
